import { useViewContext } from "@editor-providers";
import { useLoadSwiper } from "@library/shared";
import { useEffect, useRef } from "react";
import {
  CarouselAnimationTypeEnum,
  type ICarouselEditorData,
  type ICarouselSets,
  type ICarouselSettings,
} from "../types";
import { getCarouselAutoplaySpeed } from "../utils";

declare const Swiper: any;

interface IProps {
  sets: ICarouselSets;
  isFullScreen: boolean;
  editorData?: ICarouselEditorData;
  comboSettings: ICarouselSettings;
  isEditor: boolean;
  isMobile?: boolean;
}

export const useCarouselSwiper = ({
  sets,
  comboSettings,
  isFullScreen,
  editorData,
  isEditor,
  isMobile = false,
}: IProps) => {
  "use no memo";
  const isSwiperLoaded = useLoadSwiper();
  const amtOfItems = sets.length;

  const {
    carouselAnimationType,
    carouselAutoplaySpeed,
    carouselAutoplayActive,
    carouselStyle,
  } = comboSettings;

  const bgImagesRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const nextBtnRef = useRef<HTMLDivElement | null>(null);
  const prevBtnRef = useRef<HTMLDivElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const { view } = useViewContext();
  const isThumbnail = view === "thumbnail";

  const activeItemIdx = useRef<number>(0);
  const lastAmtOfItems = useRef<number>(amtOfItems);
  const ignoreSlideChange = useRef<boolean>(false);

  const itemsSwiper = useRef<any>(null);
  const bgSwiper = useRef<any>(null);

  const destorySwipers = () => {
    if (bgSwiper.current) {
      bgSwiper.current.destroy();
      bgSwiper.current = null;
    }
    if (itemsSwiper.current) {
      itemsSwiper.current.destroy();
      itemsSwiper.current = null;
    }
  };

  const onSlideChange = (inst: any) => {
    if (!editorData || !isEditor) return;

    if (activeItemIdx.current === inst.realIndex) return;
    activeItemIdx.current = inst.realIndex;

    if (ignoreSlideChange.current) {
      ignoreSlideChange.current = false;
      return;
    }

    const item = itemsRef.current?.children[0].children[inst.activeIndex];
    if (!item) return;
    const itemId = item.getAttribute("data-item-id");
    if (!itemId) return;
    editorData.setActiveItem(itemId);
  };

  const initSwipers = () => {
    if (!isSwiperLoaded || !itemsRef.current || isThumbnail) {
      return;
    }
    destorySwipers();

    const isFadeAnimation =
      comboSettings.carouselAnimationType === CarouselAnimationTypeEnum.FadeIn;

    const commonConfig: Record<string, unknown> = {
      // Fade stacks every slide in the same spot, so inter-slide spacing is
      // meaningless and only skews the fade math — keep it at 0 for fade.
      spaceBetween: isMobile && !isFadeAnimation ? 16 : 0,
      initialSlide: activeItemIdx.current,
      loop: isMobile ? false : !isEditor,
      effect: "slide",
      autoplay: undefined,
    };

    if (!isEditor && comboSettings.carouselAutoplayActive) {
      commonConfig.autoplay = {
        delay: getCarouselAutoplaySpeed(comboSettings.carouselAutoplaySpeed),
        disableOnInteraction: false,
      };
    }

    // Mobile peek (centered, partial neighbors) only applies to the slide
    // effect. Fade requires exactly one slide per view — it absolutely stacks
    // all slides, so a multi-slide peek piles every step on top of the active
    // one and bleeds through. Skip the peek for fade.
    if (isMobile && !isFadeAnimation) {
      commonConfig.slidesPerView = "auto";
      commonConfig.centeredSlides = true;
    }
    if (isFadeAnimation) {
      commonConfig.effect = "fade";
      commonConfig.fadeEffect = {
        crossFade: true,
      };
    }

    itemsSwiper.current = new Swiper(itemsRef.current, {
      ...commonConfig,
      allowTouchMove: !isEditor,
      pagination: {
        el: paginationRef.current,
        clickable: true,
        bulletClass: "carousel-bullet",
        bulletActiveClass: "carousel-bullet-active",
      },
      navigation: {
        nextEl: nextBtnRef.current,
        prevEl: prevBtnRef.current,
      },
      on: {
        slideChange: onSlideChange,
      },
    });
  };

  const updateSwipers = () => {
    if (itemsSwiper.current) {
      itemsSwiper.current.update();
    }
    if (bgSwiper.current) {
      bgSwiper.current.update();
    }
  };

  useEffect(() => {
    initSwipers();
  }, [
    carouselAnimationType,
    carouselAutoplaySpeed,
    carouselAutoplayActive,
    carouselStyle,
    isFullScreen,
    isMobile,
  ]);

  useEffect(() => {
    if (!itemsSwiper.current) {
      return;
    }

    updateSwipers();
    if (amtOfItems > lastAmtOfItems.current) {
      itemsSwiper.current.slideNext();
    }
    lastAmtOfItems.current = amtOfItems;
  }, [amtOfItems]);

  useEffect(() => {
    if (!isSwiperLoaded || isThumbnail) return;
    initSwipers();
    return () => {
      destorySwipers();
    };
  }, [isSwiperLoaded, isEditor, isThumbnail, isFullScreen, isMobile]);

  // Initialize background swiper when ref becomes available
  useEffect(() => {
    if (
      !isSwiperLoaded ||
      !isFullScreen ||
      !bgImagesRef.current ||
      !itemsSwiper.current
    ) {
      return;
    }

    if (bgSwiper.current) {
      return; // Already initialized
    }

    const bgConfig: Record<string, unknown> = {
      spaceBetween: 0,
      initialSlide: activeItemIdx.current,
      loop: isMobile ? false : !isEditor,
      effect: "slide",
    };

    if (
      comboSettings.carouselAnimationType === CarouselAnimationTypeEnum.FadeIn
    ) {
      bgConfig.effect = "fade";
      bgConfig.fadeEffect = {
        crossFade: true,
      };
    }

    bgSwiper.current = new Swiper(bgImagesRef.current, {
      ...bgConfig,
      allowTouchMove: false,
    });
    itemsSwiper.current.controller.control = bgSwiper.current;
  }, [isSwiperLoaded, isFullScreen, bgImagesRef.current, itemsSwiper.current]);

  const handleSwapNext = () => {
    if (!itemsSwiper.current) return;
    ignoreSlideChange.current = true;
    updateSwipers();
    itemsSwiper.current.slideNext();
  };

  const handleSwapPrev = () => {
    if (!itemsSwiper.current) return;
    ignoreSlideChange.current = true;
    updateSwipers();
    itemsSwiper.current.slidePrev();
  };

  return {
    bgImagesRef,
    itemsRef,
    nextBtnRef,
    prevBtnRef,
    paginationRef,

    handleSwapNext,
    handleSwapPrev,
  };
};
